import i from '../../translations/i'
import { storeLocal } from '../../helpers/stores'
import { config } from '../../config/app'

/** Как часто спрашиваем статус платежа после возврата со страницы оплаты */
export const POLL_INTERVAL_MS = 3000

/** Сколько ждём подтверждения, прежде чем сказать «оплата ещё обрабатывается» */
export const POLL_TIMEOUT_MS = 120000

export type BillingMethod = 'card' | 'sbp'

export type SubscriptionStatus = 'none' | 'active' | 'expiring' | 'expired'

export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'expired' | 'canceled' | 'refunded'

/** Код языка админки → локаль Intl: украинский в админке — ua, сербский переведён латиницей */
const locale = (): string => {
    const lang = storeLocal.get('lang') || config.defaultLanguage || 'ru'

    return ({ ua: 'uk', sr: 'sr-Latn' } as Record<string, string>)[lang] ?? lang
}

/**
 * Склонение по числу через Intl.PluralRules: в переводах лежат формы one/few/many/other,
 * а i18next в админке старый и сам так не умеет.
 */
export const plural = (n: number, key: string): string => {
    const form = `${key}.${new Intl.PluralRules(locale()).select(n)}`

    return i.exists(form) ? i.t(form, { n }) : i.t(`${key}.other`, { n })
}

export const months = (n: number): string => plural(n, 'billing:units.months')

export const days = (n: number): string => plural(n, 'billing:units.days')

/** Число в формате языка админки: 15 873, а не 15,873 */
export const formatNumber = (value: number | string | null | undefined): string => new Intl.NumberFormat(locale()).format(Number(value ?? 0))

export const formatMoney = (amount: string | number | null | undefined, currency: string = 'RUB'): string => {
    const value = Number(amount ?? 0)

    return new Intl.NumberFormat(locale(), {
        style: 'currency',
        currency,
        // «₽», а не «RUB» в английской и других локалях
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
        maximumFractionDigits: 2,
    }).format(value)
}

/** «12 октября 2027» без «г.»: дата часто стоит в конце предложения, и получалось «2027 г..» */
export const formatDate = (iso: string | null | undefined): string => iso
    ? new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(iso)).replace(/\s?[гр]\.$/u, '').replace(/\.$/, '')
    : '—'

export const formatDateTime = (iso: string | null | undefined): string => iso
    ? new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso))
    : '—'

/** Текст ошибки открытого API: {data: {message}} */
export const errorMessage = (err: any): string => err?.response?.data?.data?.message ?? i.t('billing:errors.common')

export const paymentStatusColor = (status: PaymentStatus): string => ({
    pending: 'processing',
    paid: 'success',
    failed: 'error',
    expired: 'default',
    canceled: 'default',
    refunded: 'warning',
}[status] ?? 'default')

export const subscriptionStatusColor = (status: SubscriptionStatus): string => ({
    none: 'default',
    active: 'success',
    expiring: 'warning',
    expired: 'error',
}[status] ?? 'default')

/** Чем заплатили: если сервис оплаты сообщил способ — показываем его, иначе выбранный клиентом */
export const methodLabel = (payment: any): string => {
    const type = payment.payment_type

    if (type === 'BankCard') return i.t('billing:methods.card.title')
    if (type === 'SBP') return i.t('billing:methods.sbp.title')
    if (type) return type

    return i.t(`billing:methods.${payment.method}.title`)
}

/** Администратор панели: роли нет у владельца проекта, 0 — администратор, 5 — администратор Lite */
export const isAdminRole = (): boolean => {
    const role = storeLocal.get('role')

    return !role || Number(role) === 0 || Number(role) === 5
}

/** Порог «скоро закончится» — как billing.expiring_days на сервере по умолчанию */
export const EXPIRING_DAYS = 7

/**
 * Подписка по данным, которые уже есть в админке (тариф из localStorage, срок из проекта).
 * Нужна, пока не пришёл ответ сервера или если раздел оплаты на сервере ещё не развёрнут.
 */
export const localSubscription = (level: number | null, planName: string | null, datePayEnd: number) => {
    if (!datePayEnd || datePayEnd <= 0) {
        return { level, plan_name: planName, paid_until: null, status: 'none' as SubscriptionStatus, days_left: null }
    }

    const paidUntil = new Date(datePayEnd * 1000)
    const seconds = (paidUntil.getTime() - Date.now()) / 1000

    if (seconds <= 0) {
        return { level, plan_name: planName, paid_until: paidUntil.toISOString(), status: 'expired' as SubscriptionStatus, days_left: 0 }
    }

    const left = Math.ceil(seconds / 86400)

    return {
        level,
        plan_name: planName,
        paid_until: paidUntil.toISOString(),
        status: (left <= EXPIRING_DAYS ? 'expiring' : 'active') as SubscriptionStatus,
        days_left: left,
    }
}
